/**
 * Resident Dashboard Apis
 */

import type { ApiResponse, Pagination } from '@/shared/types/api';
import type {
  AnnouncementOverview,
  CreateAnnouncementResponse,
  CreateResidentAnnouncementPayload,
  DashboardHouseDetails,
  PaymentActivity,
  ResidentDashboardOverview,
  UsePaymentActivitiesParams,
} from '../types/dashboard';
import { api } from '../../../shared/api/client';
import type { FeaturedHousesData } from '@/features/onboarding';
import type { ExploreHouseFiltersType } from '../components/dashboard/ExploreHouseFilters';
import type { FinancialFiltersType } from '@/features/financials/components/FinancialHistoryFilters';

export interface ResidentResource {
  id: string;
  title: string;
  description?: string;
  category: string;
  type?: 'document' | 'video' | 'link' | 'image';
  fileUrl?: string;
  fileSize?: number;
  uploadedBy: {
    id: string;
    name: string;
    email: string;
    avatar: string | null;
  };
  createdAt: string;
  status: 'PENDING' | 'SIGNED';
  signedAt: string | null;
  isSignable: boolean;
}

export interface GetResidentResourcesParams {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
}

export const residentDashboardApis = {
  // ✅ Get resident dashboard overview
  getOverview: async (): Promise<ResidentDashboardOverview> => {
    const res = await api.get<
      ApiResponse<{ overview: ResidentDashboardOverview }>
    >('/residents/dashboard/overview');
    return res.data.overview;
  },

  getActivities: async (
    params: UsePaymentActivitiesParams,
  ): Promise<{
    records: PaymentActivity[];
    pagination: Pagination;
  }> => {
    const searchParams = new URLSearchParams();

    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.status) searchParams.append('status', params.status);
    if (params.announcementType)
      searchParams.append('announcementType', params.announcementType);

    const res = await api.get<
      ApiResponse<{
        records: PaymentActivity[];
        pagination: Pagination;
      }>
    >(
      `/residents/dashboard/activities/${params.type.toLowerCase()}?${searchParams.toString()}`,
    );

    return res.data;
  },

  getPaymentHistory: async (
    params: FinancialFiltersType,
  ): Promise<{
    records: PaymentActivity[];
    pagination: Pagination;
  }> => {
    const searchParams = new URLSearchParams();

    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.status) searchParams.append('status', params.status);
    if (params.search) searchParams.append('search', params.search);

    const res = await api.get<
      ApiResponse<{
        records: PaymentActivity[];
        pagination: Pagination;
      }>
    >(`/residents/dashboard/payments`, {
      params: searchParams,
    });

    return res.data;
  },

  getHouses: async (filters: ExploreHouseFiltersType) => {
    const params = new URLSearchParams();

    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.search) params.append('search', filters.search);
    if (filters.location) params.append('location', filters.location);
    if (filters.priceOrder) params.append('priceOrder', filters.priceOrder);

    const res = await api.get<ApiResponse<FeaturedHousesData>>(
      `/residents/dashboard/houses/featured?${params.toString()}`,
    );

    return res.data;
  },

  getHouseById: async (houseId: string): Promise<DashboardHouseDetails> => {
    const res = await api.get<ApiResponse<{ house: DashboardHouseDetails }>>(
      `/residents/dashboard/houses/${houseId}`,
    );
    return res.data.house;
  },

  createAnnouncement: async (
    payload: CreateResidentAnnouncementPayload,
  ): Promise<CreateAnnouncementResponse> => {
    const res = await api.post<ApiResponse<CreateAnnouncementResponse>>(
      `/residents/dashboard/announcements`,
      payload,
    );
    return res.data;
  },

  getResidentAnnouncementOverview: async (): Promise<AnnouncementOverview> => {
    const res = await api.get<ApiResponse<AnnouncementOverview>>(
      `/residents/dashboard/announcements/overview`,
    );

    return res.data;
  },

  getMyResources: async (
    params: GetResidentResourcesParams,
  ): Promise<{
    resources: ResidentResource[];
    pagination: Pagination;
  }> => {
    const searchParams = new URLSearchParams();

    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.search) searchParams.append('search', params.search);
    if (params.category) searchParams.append('category', params.category);

    const res = await api.get<
      ApiResponse<{
        resources: ResidentResource[];
        pagination: Pagination;
      }>
    >(`/resources/my-resources?${searchParams.toString()}`);

    return res.data;
  },

  signResource: async (id: string, path: string): Promise<void> => {
    await api.post<ApiResponse<void>>(`/resources/${id}/sign`, { path });
  },

  getResourceById: async (id: string): Promise<ResidentResource> => {
    const res = await api.get<ApiResponse<ResidentResource>>(
      `/resources/${id}`,
    );
    return res.data;
  },

  getResidentResourceById: async (id: string): Promise<ResidentResource> => {
    const res = await api.get<ApiResponse<ResidentResource>>(
      `/resources/${id}/resident`,
    );
    return res.data;
  },

  getSignatureUploadUrl: async (
    id: string,
  ): Promise<{ signedUrl: string; path: string }> => {
    const res = await api.post<
      ApiResponse<{ signedUrl: string; path: string }>
    >(`/resources/${id}/signature-upload-url`);
    return res.data;
  },

  uploadFile: async (url: string, file: Blob): Promise<void> => {
    await fetch(url, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': 'image/png',
      },
      mode: 'cors',
    });
  },
};
